"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheckBig as CheckCircle2, FileCheck, FlaskConical, Gauge, Layers, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const capabilities = [{
  title: "HPLC & UHPLC Solutions",
  description: "High-resolution liquid chromatography systems configured for assay, purity, and impurity profiling.",
  icon: FlaskConical
}, {
  title: "Gas Chromatography (GC)",
  description: "Robust GC and headspace systems for volatile organic compound detection and solvent analysis.",
  icon: Gauge
}, {
  title: "Solvent Recovery & Efficiency",
  description: "Automated solvent recycling integrations that reduce lab waste, solvent consumption, and runtime costs.",
  icon: Layers
}];
const highlights = ["Compliant with 21 CFR Part 11 & GAMP 5 standards", "Full IQ/OQ qualification protocols and lifecycle support", "Application validation across Ireland and the UK"];
export default function Chromatography() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="chromatographyhero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 border-b border-border">
      {/* Decorative gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-radial-[at_top_right] from-secondary/60 via-transparent to-transparent" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
          {/* Left Column: Copy & Actions */}
          <motion.div className="lg:col-span-7 flex flex-col gap-6" initial="hidden" animate="visible" variants={containerVariants}>
            {/* Breadcrumb Navigation */}
            <motion.nav variants={itemVariants} aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-muted-foreground">
              <Link to="/HomePage" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <span className="text-muted-foreground/60">/</span>
              <Link to="/SolutionsPage" className="hover:text-foreground transition-colors">
                Solutions
              </Link>
              <span className="text-muted-foreground/60">/</span>
              <span className="text-foreground font-medium" aria-current="page">
                Chromatography
              </span>
            </motion.nav>

            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-xs text-secondary-foreground">
                <Activity className="size-3.5 text-primary mr-1" />
                Analytical Instrumentation
              </Badge>
              <Badge variant="outline" className="px-3 py-1 font-medium text-xs text-muted-foreground border-border">
                Pharma & Regulated Labs
              </Badge>
            </motion.div>

            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl md:text-5xl">
                Liquid and Gas Chromatography for Regulated Laboratories
              </h1>
              <p className="text-base text-muted-foreground sm:text-lg max-w-2xl leading-relaxed">
                High-precision liquid and gas chromatography platforms engineered for quantitative analytical performance, method compliance, and robust data integrity across pharmaceutical, chemical, and academic testing environments.
              </p>
            </motion.div>

            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-3 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  <span>Discuss Your Application</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent">
                <Link to="/BookADemoPage">Book a System Demo</Link>
              </Button>
            </motion.div>

            <motion.div variants={itemVariants} className="pt-4 border-t border-border/80">
              <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-1">
                {highlights.map((highlight, index) => <li key={index} data-index={index} className="flex items-center gap-2.5 text-sm text-foreground/90 font-medium">
                    <CheckCircle2 className="size-4 shrink-0 text-primary" />
                    <span>{highlight}</span>
                  </li>)}
              </ul>
            </motion.div>
          </motion.div>

          {/* Right Column: Visual Component & Floating Spec Card */}
          <motion.div className="lg:col-span-5 relative" initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }}>
            <div className="relative rounded-2xl border border-border bg-card p-3 shadow-md overflow-hidden">
              <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chromatography_system_laboratory.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chromatography_system_laboratory.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/chromatography_system_laboratory.png-wvc-sizes" alt="High performance chromatography system installed in an analytical testing laboratory" className="h-full w-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-linear-to-t from-foreground/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-background">
                  <p className="text-xs font-medium uppercase tracking-wider opacity-90 text-background">
                    Precision Separation
                  </p>
                  <p className="text-sm font-semibold text-background">
                    Automated HPLC, GC & In-Line Solvent Recovery
                  </p>
                </div>
              </div>

              {/* Floating feature pill */}
              <div className="mt-3 flex items-center justify-between rounded-lg bg-secondary/80 p-3 px-4 border border-border">
                <div className="flex items-center gap-2.5">
                  <ShieldCheck className="size-5 text-primary shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-secondary-foreground">Certified Validation</p>
                    <p className="text-xs text-muted-foreground">Full IQ/OQ documentation included</p>
                  </div>
                </div>
                <Button asChild size="sm" variant="ghost" className="h-7 px-2 text-xs font-medium">
                  <Link to="/QualificationLifecycleServicesPage">
                    <span className="flex items-center gap-1">
                      Details <FileCheck className="size-3" />
                    </span>
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Analytical Capabilities Cards Row */}
        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          {capabilities.map((item, index) => {
          const Icon = item.icon;
          return <Card key={index} data-index={index} className="group border-border bg-card transition-[border-color,box-shadow] duration-200 hover:border-primary/50 hover:shadow-md">
                <CardContent className="flex flex-col gap-3 p-6">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-primary">
                    <Icon className="size-5" />
                  </div>
                  <h3 className="text-base font-semibold text-card-foreground">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}